/**
 * The answer-rating form, shared by the public page (`/avaliar/:token`) and the run page,
 * which offers it beside the sandboxed reply preview (fr-docs `docs/smith-ams` T-117).
 *
 * It takes the context the caller already fetched (`['feedback-context', token]`), seeds the
 * form from a previous submission and the clicked star, and submits through the BFF's public
 * relay — the same route either way, because the signed token is the credential, not the
 * session. The form stays editable while the link is valid; a resubmission updates.
 */
import { Notice, Spinner } from '@evt/flow-stream-ui'
import { FEEDBACK_SUGGESTIONS_MAX, type FeedbackContextResponse, type FeedbackScore } from '@portal/contracts'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { submitFeedback } from '../api/feedback'
import { feedbackContextKey } from '../hooks/queryClient'
import { describeApiError } from '../i18n/errors'
import { useI18n } from '../i18n/I18nProvider'
import { initialForm, type RatingForm, SCORES, submitBody } from '../pages/rate/form'
import { readRatingToken } from './rating-link'

const NO_QUERY = new URLSearchParams()

export function RatingPanel(props: {
  token: string
  context: FeedbackContextResponse
  /** The public page's `?pt=` / `?as=` — the star the reader clicked in the e-mail. */
  query?: URLSearchParams
}) {
  const { token, context } = props
  const { t, formatDate } = useI18n()
  const expiresAt = readRatingToken(token)?.expiresAt
  const queryClient = useQueryClient()
  const [form, setForm] = useState<RatingForm>(() => initialForm(context, props.query ?? NO_QUERY))
  const [submitted, setSubmitted] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const send = useMutation({
    mutationFn: submitFeedback,
    onSuccess: (saved) => {
      setSubmitted(true)
      setFormError(null)
      // Keep the cached context in step, so a remount reads "already rated" and the new scores.
      queryClient.setQueryData<FeedbackContextResponse>(feedbackContextKey(token), (prev) =>
        prev
          ? {
              ...prev,
              ratings: saved.ratings,
              suggestions: form.suggestions.trim() || null,
              submissions: saved.submissions
            }
          : prev
      )
    },
    onError: (err) => setFormError(describeApiError(t, err))
  })

  function onSubmit(e: { preventDefault(): void }) {
    e.preventDefault()
    const body = submitBody(token, form)
    if (!body) {
      setFormError(context.suggestions ? t.feedback.clearNeedsRating : t.feedback.nothingToSend)
      return
    }
    setFormError(null)
    send.mutate(body)
  }

  if (submitted) {
    return (
      <div className="login-body" aria-live="polite">
        <Notice kind="success" className="login-notice">
          <strong>{t.feedback.thanksTitle}</strong>{' '}
          {expiresAt ? t.feedback.thanksBodyUntil(formatDate(expiresAt)) : t.feedback.thanksBody}
        </Notice>
        <button type="button" className="btn btn-block" onClick={() => setSubmitted(false)}>
          {t.feedback.edit}
        </button>
      </div>
    )
  }

  const charsLeft = FEEDBACK_SUGGESTIONS_MAX - form.suggestions.length
  const alreadyRated = context.submissions > 0

  return (
    <form className="login-form rate-form" onSubmit={onSubmit} noValidate>
      {alreadyRated && <p className="muted rate-note">{t.feedback.previousSubmission(context.submissions)}</p>}

      <ScoreGroup
        name={`responseTime-${token.slice(-8)}`}
        legend={t.feedback.responseTime}
        hint={t.feedback.responseTimeHint}
        value={form.responseTime}
        onChange={(v) => setForm({ ...form, responseTime: v })}
      />
      <ScoreGroup
        name={`assertiveness-${token.slice(-8)}`}
        legend={t.feedback.assertiveness}
        hint={t.feedback.assertivenessHint}
        value={form.assertiveness}
        onChange={(v) => setForm({ ...form, assertiveness: v })}
      />

      <label className="rate-field">
        <span className="field-label">
          {t.feedback.suggestions} <span className="muted">({t.common.optional})</span>
        </span>
        <textarea
          className="login-input rate-textarea"
          rows={4}
          maxLength={FEEDBACK_SUGGESTIONS_MAX}
          placeholder={t.feedback.suggestionsPlaceholder}
          value={form.suggestions}
          onChange={(e) => setForm({ ...form, suggestions: e.target.value })}
        />
        <span className="muted rate-counter">{t.feedback.charsLeft(charsLeft)}</span>
      </label>

      {formError && (
        <div role="alert">
          <Notice kind="danger" className="login-notice">
            {formError}
          </Notice>
        </div>
      )}

      <button type="submit" className="btn btn-primary btn-block" disabled={send.isPending}>
        {send.isPending ? (
          <>
            <Spinner size={14} /> {t.feedback.sending}
          </>
        ) : alreadyRated || send.isSuccess ? (
          t.feedback.update
        ) : (
          t.feedback.submit
        )}
      </button>
    </form>
  )
}

/** One 1–5 question as a native radio group: arrow keys, labels and screen readers for free. */
function ScoreGroup(props: {
  name: string
  legend: string
  hint: string
  value: FeedbackScore | undefined
  onChange: (value: FeedbackScore) => void
}) {
  const { t } = useI18n()
  return (
    <fieldset className="rate-group">
      <legend className="field-label">{props.legend}</legend>
      <p className="muted rate-hint">{props.hint}</p>
      <div className="rate-stars">
        {SCORES.map((n) => (
          <label key={n} className={`rate-star${props.value !== undefined && n <= props.value ? ' on' : ''}`}>
            <input
              type="radio"
              name={props.name}
              value={n}
              checked={props.value === n}
              onChange={() => props.onChange(n)}
              aria-label={t.feedback.scoreLabel(n)}
            />
            <span aria-hidden="true">★</span>
          </label>
        ))}
      </div>
      <div className="rate-scale muted" aria-hidden="true">
        <span>{t.feedback.scoreLow}</span>
        <span>{t.feedback.scoreHigh}</span>
      </div>
    </fieldset>
  )
}
